import { CreditPlanRequestModel, CreditPlanResponseModel } from '@/@types/client-api';
import {
  mapThumbFeedbackUIValueToThumbValue,
  mapCreditPlanFailureReason,
  mapCreditPlanStatus
} from '@/components/ui/CreditPlan/util/CreditPlanHelpers';
import type { ThumbFeedbackValue } from '@/components/ui/Feedback/ThumbFeedbackButtons';
import { ClientAPI } from '@/redux/api';

type ModifyLibraryRequest = {
  license: string;
  codes: string[];
};

type SubmitCreditPlanFeedbackRequest = {
  planId: string;
  thumbValue: ThumbFeedbackValue;
};

export type ResendCreditPlanEmailRequest = {
  processId: string;
  licenseIdentifier?: string;
};

const creditPlanEndpoints = ClientAPI.injectEndpoints({
  endpoints: builder => ({
    getCreditPlan: builder.query<CreditPlanResponseModel, string>({
      query: planId => ({
        url: `credit-plans/${planId}`,
        method: 'GET'
      }),
      providesTags: result => [
        {
          type: 'CreditPlans' as const,
          id: result?.processId ?? result?.creditPlan?.id ?? undefined
        }
      ],
      transformResponse(res: CreditPlanResponseModel) {
        return {
          ...res,
          status: mapCreditPlanStatus(res.status as unknown as number),
          failureReason: mapCreditPlanFailureReason(res.failureReason as unknown as number)
        };
      }
    }),
    getAllCreditPlans: builder.query<CreditPlanResponseModel[], void>({
      query: () => ({
        url: `credit-plans/all`,
        method: 'GET'
      }),
      providesTags: result =>
        result?.length
          ? [
              ...result.map(plan => ({
                type: 'CreditPlans' as const,
                id: plan?.processId ?? plan?.creditPlan?.id ?? undefined
              })),
              { type: 'CreditPlans' as const, id: 'LIST' }
            ]
          : [{ type: 'CreditPlans' as const, id: 'LIST' }],
      transformResponse(res: CreditPlanResponseModel[]) {
        return res.map(plan => ({
          ...plan,
          status: mapCreditPlanStatus(plan.status as unknown as number),
          failureReason: mapCreditPlanFailureReason(plan.failureReason as unknown as number)
        }));
      }
    }),
    deleteCreditPlan: builder.query<boolean, string>({
      query: planId => ({
        url: `credit-plans/${planId}`,
        method: 'DELETE'
      })
    }),
    saveAllToLibrary: builder.mutation<Record<string, boolean>, ModifyLibraryRequest>({
      query: req => ({
        url: `credit-plans/save-items/${req.license}`,
        body: [...req.codes],
        method: 'POST'
      }),
      invalidatesTags: ['Library', 'LibraryItem']
    }),
    removeAllFromLibrary: builder.mutation<Record<string, boolean>, ModifyLibraryRequest>({
      query: req => ({
        url: `credit-plans/delete-items/${req.license}`,
        body: [...req.codes],
        method: 'DELETE'
      })
    }),
    submitCreditPlanFeedback: builder.mutation<void, SubmitCreditPlanFeedbackRequest>({
      query: ({ planId, thumbValue }) => ({
        url: `credit-plans/${planId}/feedback`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(mapThumbFeedbackUIValueToThumbValue(thumbValue)),
        responseHandler: async response => {
          if (response.status === 204) return null;
          const text = await response.text();
          return text ? JSON.parse(text) : null;
        },
        validateStatus: response => response.status >= 200 && response.status < 300
      }),
      invalidatesTags: (_result, _error, { planId }) => [
        { type: 'CreditPlans' as const, id: planId },
        { type: 'CreditPlans' as const, id: 'LIST' }
      ]
    }),
    createCreditPlan: builder.mutation<
      CreditPlanResponseModel,
      CreditPlanRequestModel & { indivSk?: string }
    >({
      query: req => ({
        url: 'credit-plans',
        body: req,
        method: 'POST'
      }),
      transformResponse(res: CreditPlanResponseModel) {
        return {
          ...res,
          status: mapCreditPlanStatus(res.status as unknown as number),
          failureReason: mapCreditPlanFailureReason(res.failureReason as unknown as number)
        };
      }
    }),
    resendCreditPlanEmail: builder.mutation<{ status?: string }, ResendCreditPlanEmailRequest>({
      query: req => ({
        url: 'credit-plans/resend-email',
        body: req.processId && !req.licenseIdentifier ? { processId: req.processId } : req,
        method: 'POST',
        validateStatus: response => {
          return response.status === 200 || response.status === 202;
        }
      })
    })
  })
});

export const {
  useGetCreditPlanQuery,
  useLazyGetCreditPlanQuery,
  useGetAllCreditPlansQuery,
  useLazyDeleteCreditPlanQuery,
  useSaveAllToLibraryMutation,
  useRemoveAllFromLibraryMutation,
  useCreateCreditPlanMutation,
  useResendCreditPlanEmailMutation,
  useSubmitCreditPlanFeedbackMutation
} = creditPlanEndpoints;

export default creditPlanEndpoints;
